import { heuteBerlin } from "../welt/lage";

/** W82 Web-5h-N2b (Frage 61 = B, D-661, D-663): Lesen, Formen und Rechnen des Datumsfelds (Feld.tsx `Datumsfeld`) und des
 *  Monatsblatts (Monatsblatt.tsx). Werte nach außen immer JJJJ-MM-TT, gezeigt TT.MM.JJJJ; Namen über Intl („de-DE“). */

/** Sätze der Satzquelle Teil b (DW01 bis DW10, wortgleich; DW03 und DW07 aus Intl gebaut). */
export const DATUM_TEXTE = {
  platzhalter: "TT.MM.JJJJ",
  kalender: "Kalender",
  voriger: "Voriger Monat",
  naechster: "Nächster Monat",
  heute: "heute",
  form: "Bitte als TT.MM.JJJJ eingeben.",
  gibtEsNicht: "Diesen Tag gibt es nicht.",
};

export type Gelesen = { art: "leer" } | { art: "tag"; iso: string } | { art: "form" } | { art: "fehlt" };

const zwei = (n: number) => String(n).padStart(2, "0");

/** Tag aus Jahr, Monat (1 bis 12) und Tag als JJJJ-MM-TT, wenn es ihn gibt (Rundreise über UTC, wie vorschlag.ts `istIsoDatum`). */
function bauen(jahr: number, monat: number, tag: number): string | null {
  if (monat < 1 || monat > 12 || tag < 1) return null;
  // setUTCFullYear statt Date.UTC: dort würden die Jahre 0 bis 99 zu 1900 bis 1999 (ein getipptes „0026“ bleibt 0026)
  const d = new Date(0);
  d.setUTCFullYear(jahr, monat - 1, tag);
  if (d.getUTCFullYear() !== jahr || d.getUTCMonth() !== monat - 1 || d.getUTCDate() !== tag) return null;
  return `${String(jahr).padStart(4, "0")}-${zwei(monat)}-${zwei(tag)}`;
}

/** Getippter Text: TT.MM.JJJJ, T.M.JJJJ, TTMMJJJJ und JJJJ-MM-TT (Einfügen). „form“: so schreibt man kein Datum (DW09);
 *  „fehlt“: die Form stimmt, den Tag gibt es nicht (31.02.2026, DW10). */
export function datumLesen(text: string): Gelesen {
  const t = text.trim();
  if (!t) return { art: "leer" };
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(t) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(t);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (!m && !iso) return { art: "form" };
  const [jahr, monat, tag] = m ? [Number(m[3]), Number(m[2]), Number(m[1])] : [Number(iso?.[1]), Number(iso?.[2]), Number(iso?.[3])];
  if (jahr < 1) return { art: "fehlt" };
  const gebaut = bauen(jahr, monat, tag);
  return gebaut ? { art: "tag", iso: gebaut } : { art: "fehlt" };
}

/** JJJJ-MM-TT als TT.MM.JJJJ; alles andere bleibt, wie es ist. */
export function datumAnzeige(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/** Heute in Berlin als JJJJ-MM-TT (die App rechnet Fristen in Berliner Tagen). */
export function heuteIso(): string {
  return heuteBerlin();
}

function utc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function isoVon(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Tag plus `tage` (auch negativ). */
export function tagPlus(iso: string, tage: number): string {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + tage);
  return isoVon(d);
}

/** Gleicher Tag `monate` Monate weiter; gibt es ihn dort nicht (31. im Februar), der letzte Tag des Monats. */
export function monatPlus(iso: string, monate: number): string {
  const [j, m, t] = iso.split("-").map(Number) as [number, number, number];
  const ziel = new Date(0);
  ziel.setUTCFullYear(j, m - 1 + monate, 1);
  const ende = new Date(0);
  ende.setUTCFullYear(ziel.getUTCFullYear(), ziel.getUTCMonth() + 1, 0);
  ziel.setUTCDate(Math.min(t, ende.getUTCDate()));
  return isoVon(ziel);
}

/** Wochentag ab Montag: 0 Montag bis 6 Sonntag. */
export function wochentag(iso: string): number {
  return (utc(iso).getUTCDay() + 6) % 7;
}

/** Liegt der Tag innerhalb von `min` und `max` (je JJJJ-MM-TT oder leer)? */
export function imRahmen(iso: string, min?: string, max?: string): boolean {
  return (!min || iso >= min) && (!max || iso <= max);
}

/** Erster Tag des Monats von `iso`. */
export function monatsAnfang(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Sechs Wochen ab Montag (42 Zellen); Tage anderer Monate sind null (leere Zellen). */
export function monatsRaster(anfang: string): (string | null)[][] {
  const start = tagPlus(anfang, -wochentag(anfang));
  const monat = anfang.slice(0, 7);
  return Array.from({ length: 6 }, (_, w) =>
    Array.from({ length: 7 }, (_, d) => {
      const tag = tagPlus(start, w * 7 + d);
      return tag.slice(0, 7) === monat ? tag : null;
    }),
  );
}

const MONAT = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric", timeZone: "UTC" });
const LANG = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
const KURZ = new Intl.DateTimeFormat("de-DE", { weekday: "short", timeZone: "UTC" });
const NAME = new Intl.DateTimeFormat("de-DE", { weekday: "long", timeZone: "UTC" });

/** DW03: „Oktober 2026“. */
export function monatsName(iso: string): string {
  return MONAT.format(utc(monatsAnfang(iso)));
}

/** DW07 und DW08: „Donnerstag, 22. Oktober 2026“, am heutigen Tag mit „, heute“. */
export function tagLesetext(iso: string, heute: string): string {
  const text = LANG.format(utc(iso));
  return iso === heute ? `${text}, ${DATUM_TEXTE.heute}` : text;
}

/** DW06: Wochenkopf ab Montag, sichtbar „Mo“ bis „So“ (Intl schreibt „Mo.“), vorgelesen „Montag“ bis „Sonntag“. */
export function wochenkopf(): { kurz: string; lang: string }[] {
  // 2026-10-19 ist ein Montag
  return Array.from({ length: 7 }, (_, i) => {
    const d = utc(tagPlus("2026-10-19", i));
    return { kurz: KURZ.format(d).replace(/\.$/, ""), lang: NAME.format(d) };
  });
}
